/*
 Selector-esque
 * Using useCallback for stability so we can take advantage of react-queries memoization
*/

import { useCallback } from 'react';
import { useRouter } from 'next/router';
import get from 'lodash/get';

/* Selectors */
import useHomeHubCache from './useHomeHub.cache';
import { useUserByKeySelector } from '@store/queries/user/useUser.selector';

/* Utils & Hooks */
import answerSchemaNormalizer from '@scenes/HomeHub/homehub.answerNormalization';
import useRouteController from '@scenes/HomeHub/hooks/hook.routeController';
import { Logger } from '@utils/logger';

import { TSurveyQuestion, TSurveyShort } from '@scenes/HomeHub/homehub.types';
import { SURVEY_STATUSES } from '@scenes/HomeHub/homehub.constants';

/*
 * useHomeHubByKeySelector
 * - Gets a key in the home hub cache.
 *
 * If you want just one key value from the Cart cache.
 * const id = useHomeHubByKeySelector('id');
 * const answers = useHomeHubByKeySelector('survey.answers');
 * */
export const useHomeHubByKeySelector = (key: string) => useHomeHubCache(useCallback((data: any) => get(data, key), [key]));

/**
 * useHomeHubDataSelector
 *
 * These will get all the data that is saved to cache. Need this to explicitly set within in.
 */
export const useHomeHubDataSelector = () => useHomeHubCache<any>((data: any) => data || {});

/**
 * useHomeHubQuestionSelector
 *
 * Given the query params, match it with the question, and return it.
 *
 * @param {any} fq - foundQuestion
 * @param {any} fad
 * @param {any} seo
 * @returns {any}
 */
type TAnswerDatWrapper = {
  fq: TSurveyQuestion;
  fad: Record<string, any>;
  seo: string;
};

const formatAnswerDataWrapper = ({ fq, fad, seo }: TAnswerDatWrapper) => {
  try {
    return fad.get(fq?.question_type)?.(seo) ?? {};
  } catch (err) {
    Logger('formatAnswerDataWrapper')(err as Error);
    return {};
  }
};

export const useHomeHubQuestionSelector = (questionQuery?: string) => {
  const { query } = useRouter();
  const hid = useUserByKeySelector('account.hub.id');

  return useHomeHubCache<any>((data: any) => {
    const questionType = query?.question || '';
    const currentSurvey = data?.currentSurvey;
    const getFirstSuperstitial = currentSurvey?.questions?.[0] || {};
    const foundQuestion =
      currentSurvey?.questions?.find((question: TSurveyQuestion) => [questionQuery, questionType].includes(question?.data_type)) ||
      getFirstSuperstitial;
    const seoName = currentSurvey?.seo_name;
    const surveyQuestion = {
      ...foundQuestion,
      seoName,
      /*
        This allows us to transform the data going into answers & params for the update api.
        We'll get:
          read(): We read the answers and format so the consuming component can use it.
          write(): We read the answers and transform them and slot them into query state (thus the "write").
          params(): We read the answers and transform them and slot them into params for the updateMutation (thus the "params").
      */
      answerSchemaNormalizer: questionType
        ? formatAnswerDataWrapper({
            fq: foundQuestion,
            fad: answerSchemaNormalizer({
              currentQuestionId: foundQuestion?.id,
              currentSelectedAnswers: foundQuestion.selected_answers,
              questionSeoName: foundQuestion?.surveyType,
              hid,
            }),
            seo: foundQuestion?.surveyType || seoName,
          })
        : {},
    };

    return surveyQuestion || {};
  });
};

/* Hand back the users progress in the flow: This is not a answer completed progress */
export const useAnswerCompletePercentage = (includePercentage: boolean = true) => {
  const { query } = useRouter();

  return useHomeHubCache<any>((data: any) => {
    const currentSurvey = data?.currentSurvey;
    const isAnswerCompletedCount = data?.currentSurvey?.isAnswerCompletedCount || 0;
    const currentQuestionQuery = query?.question;
    const questions = currentSurvey?.questions?.filter((q: TSurveyQuestion) => !q.data_type?.includes('superstitial')) || [];
    const findCurrentPlaceInFlow = questions?.findIndex((q: TSurveyQuestion) => q?.data_type === currentQuestionQuery) || 0;
    const completedPercentage = Math.round(
      (100 / (questions.length + isAnswerCompletedCount)) * (findCurrentPlaceInFlow + isAnswerCompletedCount + 1),
    );

    return includePercentage && completedPercentage ? `${completedPercentage}%` : '';
  });
};

/**
 * useHomeHubOutOfSyncSelector
 * - Returns true if the home hub is out of sync with the flow and their current server answer state
 *    - For instance, if they land on question 5, but their server state is on question 2, then we're out of sync.
 */
export const useHomeHubOutOfSyncSelector = () => {
  const { query } = useRouter();
  const { explicitRoute, recommendationRoute } = useRouteController();

  return useHomeHubCache<any>((data: any) => {
    const currentSurvey = data?.currentSurvey;
    const isCurrentSurveyComplete = currentSurvey?.status === SURVEY_STATUSES.completed;
    const currentQuestionQuery = query?.question;
    const questions = currentSurvey?.questions?.filter((q: TSurveyQuestion) => !q.data_type?.includes('superstitial')) || [];
    const findCurrentPlaceInFlow = questions?.findIndex((q: TSurveyQuestion) => q?.data_type === currentQuestionQuery) || 0;
    const reduceQuestionSetToSearch = questions?.slice(0, findCurrentPlaceInFlow);
    /* If the question is in the excluded list, do not include in requirement for going back to it. */
    const removeExcludedQuestionIds = reduceQuestionSetToSearch?.filter(
      (q: TSurveyQuestion) => !currentSurvey?.excepted_questions_ids?.includes(q.id),
    );

    /* determine if any previous questions are unaswered within flow. If so, go to it */
    const hasUnansweredPreviousQuestions = removeExcludedQuestionIds?.find((q: TSurveyQuestion) => !q?.selected_answers?.length && q.required);

    /* User completed current survey, redirect to their recommendations */
    if (isCurrentSurveyComplete) {
      recommendationRoute(currentSurvey.seo_name);
    }

    /* User is on a question that has previous questions unanswered */
    if (findCurrentPlaceInFlow > 0 && hasUnansweredPreviousQuestions?.data_type) {
      return explicitRoute(hasUnansweredPreviousQuestions.data_type);
    }

    return null;
  });
};

/**
 * useHomeHubUserHasInteractedSurveys
 *
 * - Returns boolean if a user has a completed or started survey
 */
export const useHomeHubUserHasInteractedSurveys = () =>
  useHomeHubCache<any>((data: any) => {
    const { surveys } = data;
    const hasInteracted = surveys?.some((s: TSurveyShort) => [SURVEY_STATUSES.completed, SURVEY_STATUSES.started].includes(s.status));

    return hasInteracted;
  });

/**
 * useHomeHubHasQuestionsSelector
 *
 * - Returns boolean if question is empty, this would happen after a completed survey.
 */
export const useHomeHubHasQuestionsSelector = () =>
  useHomeHubCache<any>((data: any) => {
    const { currentSurvey } = data;
    const hasQuestions = currentSurvey?.questions?.length > 0;

    return hasQuestions;
  });

export const useHomeHubHasSelectedAnswersSelector = () => {
  const { query } = useRouter();

  return useHomeHubCache<any>((data: any): boolean => {
    const { currentSurvey } = data;
    const hasSelectedAnswers = currentSurvey?.questions?.find((q: TSurveyQuestion) => q?.selected_answers?.length && q?.data_type === query.question);

    return Boolean(hasSelectedAnswers);
  });
};
